import { onCLS, onINP, onFCP, onLCP, onTTFB, Metric } from 'web-vitals';
import { Sentry } from '@/utils/sentry';
import { logger } from '@/utils/logger';
import React from 'react';

// Import performance budgets from centralized config
import { PERFORMANCE_BUDGETS } from './performance-budgets';

// Use web vitals budgets
const BUDGETS = {
  LCP: PERFORMANCE_BUDGETS.webVitals.LCP.target,
  INP: PERFORMANCE_BUDGETS.webVitals.INP.target,
  CLS: PERFORMANCE_BUDGETS.webVitals.CLS.target,
  FCP: PERFORMANCE_BUDGETS.webVitals.FCP.target,
  TTFB: PERFORMANCE_BUDGETS.webVitals.TTFB.target,
};

class PerformanceMonitor {
  private measurements: Map<string, Metric> = new Map();
  
  init() {
    // Web Vitals monitoring
    onCLS(this.handleMetric.bind(this));
    onINP(this.handleMetric.bind(this));
    onFCP(this.handleMetric.bind(this));
    onLCP(this.handleMetric.bind(this));
    onTTFB(this.handleMetric.bind(this));
    
    // Bundle size monitoring
    this.trackBundleSize();
    
    // Assessment completion tracking
    this.setupAssessmentTracking();
  }
  
  private handleMetric(metric: Metric) {
    const metricName = metric.name;
    
    // Safe validation - check if metric is properly defined
    if (!metric || !metricName) {
      logger.warn('Invalid performance metric received:', metric);
      return;
    }
    
    // Store measurement
    this.measurements.set(metricName, metric);
    
    // Log in development for visibility
    logger.debug(`Performance: ${metricName}`, {
      value: Math.round(metric.value),
      rating: metric.rating,
      delta: Math.round(metric.delta),
    });
    
    // Send to Sentry for monitoring
    Sentry.addBreadcrumb({
      category: 'performance',
      message: `${metricName}: ${Math.round(metric.value)}`,
      level: metric.rating === 'poor' ? 'warning' : 'info',
      data: {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta
      }
    });
    
    // Log poor performance
    if (metric.rating === 'poor') {
      logger.warn(`Poor ${metricName} performance`, {
        value: Math.round(metric.value),
        rating: metric.rating,
      });
      
      Sentry.captureMessage(`Performance Issue: ${metricName}`, {
        level: 'warning',
        tags: {
          metric: metricName,
          rating: metric.rating
        },
        extra: {
          value: metric.value,
          delta: metric.delta,
          id: metric.id,
        }
      });
    }
  }
  
  private trackBundleSize() {
    if (typeof window !== 'undefined' && 'performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (navigation) {
        const bundleSize = navigation.transferSize || 0;
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        
        logger.debug('Bundle loaded', {
          sizeKB: (bundleSize / 1024).toFixed(2),
          loadTimeMs: Math.round(loadTime),
        });
        
        Sentry.addBreadcrumb({
          category: 'performance',
          message: `Bundle loaded: ${(bundleSize / 1024).toFixed(2)}KB in ${loadTime}ms`,
          level: 'info',
          data: {
            bundleSize,
            loadTime,
            compressionRatio: navigation.encodedBodySize ? 
              navigation.decodedBodySize / navigation.encodedBodySize : 0
          }
        });
      }
    }
  }
  
  private setupAssessmentTracking() {
    // Track assessment completion times
    const startTimes = new Map<string, number>();
    
    window.addEventListener('assessment-start', ((e: CustomEvent) => {
      const { moduleId } = e.detail;
      startTimes.set(moduleId, Date.now());
    }) as EventListener);
    
    window.addEventListener('assessment-complete', ((e: CustomEvent) => {
      const { moduleId, questionCount } = e.detail;
      const startTime = startTimes.get(moduleId);
      
      if (startTime) {
        const duration = Date.now() - startTime;
        const avgTimePerQuestion = duration / questionCount;
        
        logger.debug('Assessment completed', {
          moduleId,
          durationMs: duration,
          questionCount,
          avgTimePerQuestion: Math.round(avgTimePerQuestion),
        });
        
        Sentry.addBreadcrumb({
          category: 'assessment',
          message: `Assessment completed: ${moduleId}`,
          level: 'info',
          data: {
            moduleId,
            duration,
            questionCount,
            avgTimePerQuestion
          }
        });
        
        startTimes.delete(moduleId);
      }
    }) as EventListener);
  }
  
  // Get current performance snapshot
  getMetrics() {
    return Object.fromEntries(this.measurements);
  }
  
  // Check if current metrics meet budgets
  checkBudgets() {
    const violations: string[] = [];
    
    this.measurements.forEach((metric, key) => {
      const budget = BUDGETS[key as keyof typeof BUDGETS];
      if (budget && metric.value > budget) {
        violations.push(`${key}: ${metric.value} > ${budget}`);
      }
    });
    
    return {
      passing: violations.length === 0,
      violations
    };
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Utility functions for components
export function startAssessmentTiming(moduleId: string) {
  window.dispatchEvent(new CustomEvent('assessment-start', {
    detail: { moduleId }
  }));
}

export function endAssessmentTiming(moduleId: string, questionCount: number) {
  window.dispatchEvent(new CustomEvent('assessment-complete', {
    detail: { moduleId, questionCount }
  }));
}

export function measureComponentLoad(componentName: string) {
  return function<T extends React.ComponentType<any>>(Component: T): T {
    const MeasuredComponent = (props: any) => {
      const startTime = performance.now();
      
      React.useEffect(() => {
        const endTime = performance.now();
        const loadTime = endTime - startTime;
        
        if (loadTime > 100) { // Only log slow components
          logger.debug(`Component loaded: ${componentName}`, {
            loadTimeMs: loadTime.toFixed(2),
          });
          
          Sentry.addBreadcrumb({
            category: 'component-performance',
            message: `${componentName} loaded in ${loadTime.toFixed(2)}ms`,
            level: loadTime > 500 ? 'warning' : 'info',
            data: { componentName, loadTime }
          });
        }
      }, []);
      
      return React.createElement(Component, props);
    };
    
    MeasuredComponent.displayName = `Measured(${componentName})`;
    return MeasuredComponent as T;
  };
}